import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Save, Plus, Trash2, Upload, ExternalLink, Copy, Check,
  Instagram, Facebook, Linkedin, Youtube, Twitter, Link2, Loader2, X, Bot, CalendarClock, Check as CheckIcon,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import CardPreview from "@/components/xuma/CardPreview";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const SOCIALS = [
  { value: "instagram", label: "Instagram" },
  { value: "facebook", label: "Facebook" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "youtube", label: "YouTube" },
  { value: "twitter", label: "X / Twitter" },
  { value: "tiktok", label: "TikTok" },
  { value: "website", label: "Otro enlace" },
];
const PALETTES = [
  { primary: "#E25A38", background: "#1C1917", accent: "#F4A261" },
  { primary: "#1F6F68", background: "#0F2E2B", accent: "#D9A441" },
  { primary: "#6D28D9", background: "#1E1B4B", accent: "#A78BFA" },
  { primary: "#2563EB", background: "#0F172A", accent: "#60A5FA" },
  { primary: "#DB2777", background: "#500724", accent: "#F9A8D4" },
  { primary: "#F97316", background: "#FFFFFF", accent: "#1C1917" },
];

export const socialIcon = (p) => ({
  instagram: Instagram, facebook: Facebook, linkedin: Linkedin,
  youtube: Youtube, twitter: Twitter,
}[p] || Link2);

export default function CardEditor() {
  const navigate = useNavigate();
  const [card, setCard] = useState(null);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [uploading, setUploading] = useState(null);
  const avatarRef = useRef();
  const photoRef = useRef();
  const [calStatus, setCalStatus] = useState({ configured: false, connected: false });

  useEffect(() => {
    api.get("/card/me").then(({ data }) => setCard(data)).catch(() => navigate("/dashboard"));
    api.get("/calendar/status").then(({ data }) => setCalStatus(data)).catch(() => {});
    const params = new URLSearchParams(window.location.search);
    const gcal = params.get("gcal");
    if (gcal === "connected") { toast.success("Google Calendar conectado"); api.get("/calendar/status").then(({ data }) => setCalStatus(data)); }
    else if (gcal === "error") toast.error("No se pudo conectar Google Calendar");
    if (gcal) window.history.replaceState(null, "", "/editor");
  }, [navigate]);

  const setAgenda = (patch) => setCard((c) => ({ ...c, agenda: { ...(c.agenda || {}), ...patch } }));
  const connectGoogle = async () => {
    try {
      const { data } = await api.get("/oauth/calendar/login");
      window.location.href = data.authorization_url;
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Google Calendar no disponible"); }
  };
  const disconnectGoogle = async () => {
    await api.post("/calendar/disconnect");
    setCalStatus({ ...calStatus, connected: false });
    toast.success("Google Calendar desconectado");
  };

  const set = (patch) => setCard((c) => ({ ...c, ...patch }));
  const publicUrl = card ? `${window.location.origin}/c/${card.slug}` : "";

  const save = async (extra = {}, silent = false) => {
    setSaving(true);
    try {
      const payload = { ...card, ...extra };
      const { data } = await api.put("/card/me", payload);
      setCard(data);
      if (!silent) toast.success("Cambios guardados");
      return data;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (val) => {
    const data = await save({ published: val }, true);
    if (data) toast.success(val ? "Tarjeta publicada" : "Tarjeta despublicada");
  };

  const upload = async (file, kind) => {
    if (!file) return;
    setUploading(kind);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/card/me/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = `${BACKEND}${data.url}`;
      if (kind === "avatar") set({ avatar: url });
      else set({ photos: [...(card.photos || []), url].slice(0, 5) });
      toast.success("Imagen subida");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error al subir");
    } finally {
      setUploading(null);
    }
  };

  const copyUrl = async () => {
    await navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (!card) {
    return (
      <div className="min-h-screen bg-xuma-dash grid place-items-center">
        <Loader2 className="h-8 w-8 animate-spin text-xuma-primary" />
      </div>
    );
  }

  const links = card.links || [];
  const photos = card.photos || [];
  const socials = card.socials || [];

  return (
    <div className="min-h-screen bg-xuma-dash">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-white border-b border-xuma-border">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between gap-3">
          <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm text-xuma-muted hover:text-xuma-dark">
            <ArrowLeft className="h-4 w-4" /> Panel
          </Link>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Switch data-testid="publish-toggle" checked={!!card.published} onCheckedChange={togglePublish} />
              <span className="text-sm text-xuma-muted">{card.published ? "Publicada" : "Borrador"}</span>
            </div>
            <Button data-testid="save-card-btn" onClick={() => save()} disabled={saving}
              className="bg-xuma-primary hover:bg-xuma-primaryHover text-white rounded-full gap-1.5">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Guardar
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 py-8 grid lg:grid-cols-2 gap-8">
        {/* FORM */}
        <div>
          <h1 className="font-heading font-extrabold text-2xl text-xuma-dark mb-6">Editor de tarjeta</h1>
          <Tabs defaultValue="datos">
            <TabsList className="grid grid-cols-4 sm:grid-cols-7 w-full bg-white border border-xuma-border h-auto">
              <TabsTrigger value="datos" data-testid="tab-datos">Datos</TabsTrigger>
              <TabsTrigger value="diseno" data-testid="tab-diseno">Diseño</TabsTrigger>
              <TabsTrigger value="enlaces" data-testid="tab-enlaces">Enlaces</TabsTrigger>
              <TabsTrigger value="fotos" data-testid="tab-fotos">Fotos</TabsTrigger>
              <TabsTrigger value="redes" data-testid="tab-redes">Redes</TabsTrigger>
              <TabsTrigger value="asistente" data-testid="tab-asistente">Asistente</TabsTrigger>
              <TabsTrigger value="agenda" data-testid="tab-agenda">Agenda</TabsTrigger>
            </TabsList>

            {/* DATOS */}
            <TabsContent value="datos" className="mt-5 space-y-4">
              <Field label="Nombre">
                <Input data-testid="field-name" value={card.name || ""} onChange={(e) => set({ name: e.target.value })} />
              </Field>
              <Field label="Profesión o rubro">
                <Input data-testid="field-profession" value={card.profession || ""} onChange={(e) => set({ profession: e.target.value })} />
              </Field>
              <Field label="Negocio / organización">
                <Input data-testid="field-organization" value={card.organization || ""} onChange={(e) => set({ organization: e.target.value })} />
              </Field>
              <Field label="Slogan">
                <Input data-testid="field-slogan" value={card.slogan || ""} onChange={(e) => set({ slogan: e.target.value })} />
              </Field>
              <Field label="Brief · ¿qué haces?">
                <Textarea data-testid="field-brief" rows={3} value={card.brief || ""} onChange={(e) => set({ brief: e.target.value })} />
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Teléfono"><Input data-testid="field-phone" value={card.phone || ""} onChange={(e) => set({ phone: e.target.value })} placeholder="+52..." /></Field>
                <Field label="WhatsApp"><Input data-testid="field-whatsapp" value={card.whatsapp || ""} onChange={(e) => set({ whatsapp: e.target.value })} placeholder="+52..." /></Field>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Correo"><Input data-testid="field-email" value={card.email || ""} onChange={(e) => set({ email: e.target.value })} /></Field>
                <Field label="Sitio web"><Input data-testid="field-website" value={card.website || ""} onChange={(e) => set({ website: e.target.value })} placeholder="https://" /></Field>
              </div>
              <Field label="Enlace de tu tarjeta (URL)">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-xuma-muted">/c/</span>
                  <Input data-testid="field-slug" value={card.slug || ""} onChange={(e) => set({ slug: e.target.value })} />
                </div>
              </Field>
            </TabsContent>

            {/* DISEÑO */}
            <TabsContent value="diseno" className="mt-5 space-y-6">
              <div>
                <Label className="mb-2 block">Paletas rápidas</Label>
                <div className="flex flex-wrap gap-2">
                  {PALETTES.map((p, i) => (
                    <button key={i} data-testid={`palette-${i}`} onClick={() => set({ colors: p })}
                      className="h-10 w-10 rounded-lg border border-xuma-border overflow-hidden flex hover:scale-105 transition-transform"
                      style={{ background: p.background }} aria-label={`Paleta ${i + 1}`}>
                      <span className="h-full w-1/3" style={{ background: p.primary }} />
                      <span className="h-full w-1/3" style={{ background: p.accent }} />
                    </button>
                  ))}
                </div>
              </div>
              <ColorInput label="Color principal" value={card.colors?.primary} onChange={(v) => set({ colors: { ...card.colors, primary: v } })} testid="color-primary" />
              <ColorInput label="Fondo de la tarjeta" value={card.colors?.background} onChange={(v) => set({ colors: { ...card.colors, background: v } })} testid="color-background" />
              <ColorInput label="Color de acento" value={card.colors?.accent} onChange={(v) => set({ colors: { ...card.colors, accent: v } })} testid="color-accent" />
            </TabsContent>

            {/* ENLACES */}
            <TabsContent value="enlaces" className="mt-5 space-y-3">
              <p className="text-sm text-xuma-muted">Agrega hasta 5 enlaces personalizados.</p>
              {links.map((l, i) => (
                <div key={i} className="rounded-xl border border-xuma-border bg-white p-3 space-y-2" data-testid={`link-row-${i}`}>
                  <div className="flex gap-2">
                    <Input placeholder="Título (ej. Mi portafolio)" value={l.label || ""}
                      onChange={(e) => setArr("links", i, { ...l, label: e.target.value })} />
                    <Button variant="ghost" size="icon" onClick={() => delArr("links", i)} data-testid={`remove-link-${i}`}>
                      <Trash2 className="h-4 w-4 text-xuma-muted" />
                    </Button>
                  </div>
                  <Input placeholder="https://" value={l.url || ""} onChange={(e) => setArr("links", i, { ...l, url: e.target.value })} />
                </div>
              ))}
              {links.length < 5 && (
                <Button variant="outline" data-testid="add-link-btn" onClick={() => set({ links: [...links, { label: "", url: "" }] })}
                  className="w-full rounded-xl border-dashed gap-1.5"><Plus className="h-4 w-4" /> Agregar enlace</Button>
              )}
            </TabsContent>

            {/* FOTOS */}
            <TabsContent value="fotos" className="mt-5 space-y-6">
              <div>
                <Label className="mb-2 block">Foto de perfil</Label>
                <div className="flex items-center gap-4">
                  {card.avatar ? (
                    <div className="relative">
                      <img src={card.avatar} alt="" className="h-16 w-16 rounded-full object-cover" />
                      <button onClick={() => set({ avatar: null })} className="absolute -top-1 -right-1 bg-white border border-xuma-border rounded-full p-0.5" data-testid="remove-avatar">
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ) : (
                    <div className="h-16 w-16 rounded-full bg-xuma-dash grid place-items-center text-xuma-muted"><Upload className="h-5 w-5" /></div>
                  )}
                  <input ref={avatarRef} type="file" accept="image/*" hidden onChange={(e) => upload(e.target.files[0], "avatar")} />
                  <Button variant="outline" data-testid="upload-avatar-btn" onClick={() => avatarRef.current?.click()} disabled={uploading === "avatar"}>
                    {uploading === "avatar" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Subir foto"}
                  </Button>
                </div>
              </div>
              <div>
                <Label className="mb-2 block">Galería (hasta 5)</Label>
                <div className="grid grid-cols-3 gap-3">
                  {photos.map((p, i) => (
                    <div key={i} className="relative aspect-square rounded-xl overflow-hidden" data-testid={`photo-${i}`}>
                      <img src={p} alt="" className="h-full w-full object-cover" />
                      <button onClick={() => set({ photos: photos.filter((_, x) => x !== i) })}
                        className="absolute top-1 right-1 bg-white/90 rounded-full p-1" data-testid={`remove-photo-${i}`}>
                        <Trash2 className="h-3.5 w-3.5 text-xuma-primary" />
                      </button>
                    </div>
                  ))}
                  {photos.length < 5 && (
                    <button onClick={() => photoRef.current?.click()} data-testid="add-photo-btn"
                      className="aspect-square rounded-xl border-2 border-dashed border-xuma-border grid place-items-center text-xuma-muted hover:border-xuma-primary transition-colors">
                      {uploading === "photo" ? <Loader2 className="h-5 w-5 animate-spin" /> : <Plus className="h-5 w-5" />}
                    </button>
                  )}
                </div>
                <input ref={photoRef} type="file" accept="image/*" hidden onChange={(e) => upload(e.target.files[0], "photo")} />
              </div>
            </TabsContent>

            {/* REDES */}
            <TabsContent value="redes" className="mt-5 space-y-3">
              <p className="text-sm text-xuma-muted">Conecta tus redes sociales.</p>
              {socials.map((s, i) => (
                <div key={i} className="flex gap-2" data-testid={`social-row-${i}`}>
                  <Select value={s.platform || "instagram"} onValueChange={(v) => setArr("socials", i, { ...s, platform: v })}>
                    <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SOCIALS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input placeholder="https://" value={s.url || ""} onChange={(e) => setArr("socials", i, { ...s, url: e.target.value })} />
                  <Button variant="ghost" size="icon" onClick={() => delArr("socials", i)} data-testid={`remove-social-${i}`}>
                    <Trash2 className="h-4 w-4 text-xuma-muted" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" data-testid="add-social-btn" onClick={() => set({ socials: [...socials, { platform: "instagram", url: "" }] })}
                className="w-full rounded-xl border-dashed gap-1.5"><Plus className="h-4 w-4" /> Agregar red</Button>
            </TabsContent>

            {/* ASISTENTE */}
            <TabsContent value="asistente" className="mt-5 space-y-4">
              <div className="rounded-xl bg-xuma-primary/5 border border-xuma-primary/20 p-4 flex items-start gap-3">
                <span className="h-8 w-8 rounded-lg bg-xuma-primary grid place-items-center text-white shrink-0"><Bot className="h-4 w-4" /></span>
                <div>
                  <p className="text-sm font-heading font-bold text-xuma-dark flex items-center gap-2">Asistente Inteligente <span className="text-[10px] uppercase tracking-wider bg-xuma-primary text-white rounded-full px-2 py-0.5">Premium</span></p>
                  <p className="text-xs text-xuma-muted mt-0.5">Responde preguntas con tus instrucciones y capta prospectos a tu CRM automáticamente.</p>
                </div>
              </div>
              <div className="flex items-center justify-between rounded-xl border border-xuma-border bg-white p-4">
                <span className="text-sm font-medium text-xuma-dark">Activar asistente en mi tarjeta</span>
                <Switch data-testid="assistant-enabled" checked={!!card.assistant?.enabled}
                  onCheckedChange={(v) => set({ assistant: { ...card.assistant, enabled: v } })} />
              </div>
              <Field label="Nombre del asistente">
                <Input data-testid="assistant-name" value={card.assistant?.name || ""}
                  onChange={(e) => set({ assistant: { ...card.assistant, name: e.target.value } })} />
              </Field>
              <Field label="Mensaje de bienvenida">
                <Input data-testid="assistant-greeting" value={card.assistant?.greeting || ""}
                  onChange={(e) => set({ assistant: { ...card.assistant, greeting: e.target.value } })} />
              </Field>
              <Field label="Instrucciones (¿cómo debe responder?)">
                <Textarea data-testid="assistant-prompt" rows={4} value={card.assistant?.prompt || ""}
                  onChange={(e) => set({ assistant: { ...card.assistant, prompt: e.target.value } })}
                  placeholder="Ej. Eres amable y profesional. Ofrecemos consultoría para PyMEs. Horario Lun-Vie 9-18h..." />
              </Field>
              <Field label="Base de conocimiento (precios, servicios, FAQ...)">
                <Textarea data-testid="assistant-knowledge" rows={5} value={card.assistant?.knowledge || ""}
                  onChange={(e) => set({ assistant: { ...card.assistant, knowledge: e.target.value } })}
                  placeholder="Ej. Paquete básico $5,000 MXN/mes. Diagnóstico inicial gratis. Cobertura CDMX..." />
              </Field>
            </TabsContent>

            {/* AGENDA */}
            <TabsContent value="agenda" className="mt-5 space-y-4">
              <div className="rounded-xl bg-xuma-primary/5 border border-xuma-primary/20 p-4 flex items-start gap-3">
                <span className="h-8 w-8 rounded-lg bg-xuma-primary grid place-items-center text-white shrink-0"><CalendarClock className="h-4 w-4" /></span>
                <div>
                  <p className="text-sm font-heading font-bold text-xuma-dark flex items-center gap-2">Agenda de citas <span className="text-[10px] uppercase tracking-wider bg-xuma-primary text-white rounded-full px-2 py-0.5">Premium</span></p>
                  <p className="text-xs text-xuma-muted mt-0.5">Define tus horarios; tus visitantes y tu asistente podrán agendar citas disponibles.</p>
                </div>
              </div>
              <div className="flex items-center justify-between rounded-xl border border-xuma-border bg-white p-4">
                <span className="text-sm font-medium text-xuma-dark">Activar agenda de citas</span>
                <Switch data-testid="agenda-enabled" checked={!!card.agenda?.enabled}
                  onCheckedChange={(v) => setAgenda({ enabled: v })} />
              </div>
              <div>
                <Label className="mb-2 block">Días disponibles</Label>
                <div className="flex gap-2">
                  {[["Lun", 0], ["Mar", 1], ["Mié", 2], ["Jue", 3], ["Vie", 4], ["Sáb", 5], ["Dom", 6]].map(([lbl, d]) => {
                    const active = (card.agenda?.days || []).includes(d);
                    return (
                      <button key={d} type="button" data-testid={`agenda-day-${d}`}
                        onClick={() => { const days = new Set(card.agenda?.days || []); active ? days.delete(d) : days.add(d); setAgenda({ days: [...days].sort() }); }}
                        className={`h-10 w-12 rounded-xl text-sm font-medium transition-colors ${active ? "bg-xuma-primary text-white" : "bg-white border border-xuma-border text-xuma-muted"}`}>
                        {lbl}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <Field label="Desde"><Input data-testid="agenda-start" type="time" value={card.agenda?.start || "09:00"} onChange={(e) => setAgenda({ start: e.target.value })} /></Field>
                <Field label="Hasta"><Input data-testid="agenda-end" type="time" value={card.agenda?.end || "18:00"} onChange={(e) => setAgenda({ end: e.target.value })} /></Field>
                <Field label="Duración (min)">
                  <Select value={String(card.agenda?.slot_minutes || 30)} onValueChange={(v) => setAgenda({ slot_minutes: parseInt(v) })}>
                    <SelectTrigger data-testid="agenda-slot"><SelectValue /></SelectTrigger>
                    <SelectContent>{[15, 30, 45, 60, 90].map((m) => <SelectItem key={m} value={String(m)}>{m} min</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
              </div>

              {/* Google Calendar */}
              <div className="rounded-xl border border-xuma-border bg-white p-4">
                <p className="text-sm font-heading font-bold text-xuma-dark">Google Calendar</p>
                <p className="text-xs text-xuma-muted mt-0.5 mb-3">Conecta tu cuenta para sincronizar citas y bloquear tus horas ocupadas.</p>
                {!calStatus.configured ? (
                  <p className="text-xs text-xuma-muted bg-xuma-dash rounded-lg p-3">La integración con Google Calendar estará disponible en breve. La agenda interna ya funciona.</p>
                ) : calStatus.connected ? (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-green-700 flex items-center gap-1.5"><CheckIcon className="h-4 w-4" /> Conectado</span>
                    <Button variant="outline" size="sm" data-testid="gcal-disconnect" onClick={disconnectGoogle}>Desconectar</Button>
                  </div>
                ) : (
                  <Button variant="outline" data-testid="gcal-connect" onClick={connectGoogle} className="gap-2">
                    <CalendarClock className="h-4 w-4" /> Conectar Google Calendar
                  </Button>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* PREVIEW */}
        <div className="lg:sticky lg:top-24 self-start">
          <div className="rounded-3xl bg-xuma-dash border border-xuma-border p-8">
            <p className="text-xs uppercase tracking-[0.2em] text-xuma-muted text-center mb-6">Vista previa en vivo</p>
            <CardPreview card={card} publicUrl={publicUrl} />
            <div className="mt-6 flex items-center gap-2 rounded-xl bg-white border border-xuma-border p-2">
              <span className="text-xs text-xuma-muted truncate flex-1 pl-2">{publicUrl}</span>
              <Button size="sm" variant="ghost" onClick={copyUrl} data-testid="copy-url-btn" className="gap-1">
                {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
              </Button>
              <a href={publicUrl} target="_blank" rel="noreferrer">
                <Button size="sm" variant="ghost" data-testid="open-public-btn" className="gap-1"><ExternalLink className="h-3.5 w-3.5" /></Button>
              </a>
            </div>
            {!card.published && (
              <p className="mt-3 text-xs text-center text-xuma-muted">Publica tu tarjeta para que el enlace funcione.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  function setArr(key, i, val) {
    const arr = [...(card[key] || [])];
    arr[i] = val;
    set({ [key]: arr });
  }
  function delArr(key, i) {
    set({ [key]: (card[key] || []).filter((_, x) => x !== i) });
  }
}

const Field = ({ label, children }) => (
  <div>
    <Label className="mb-1.5 block">{label}</Label>
    {children}
  </div>
);

const ColorInput = ({ label, value, onChange, testid }) => (
  <div>
    <Label className="mb-1.5 block">{label}</Label>
    <div className="flex items-center gap-3">
      <input type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value)}
        data-testid={testid} className="h-10 w-14 rounded-lg border border-xuma-border cursor-pointer bg-white" />
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-32 uppercase" />
    </div>
  </div>
);
